import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useEmoji } from '@/composables/useEmoji';
import { useViewerItemStore } from '../store/useViewerItemStore';
import { useReactionsStore, ReactionTimelineEvent } from '../store/useReactionsStore';

const MAX_BUCKET_COUNT = 12;
const MIN_BUCKET_COUNT = 3;
// Roughly the rendered width of a marker pill (emoji + count + padding) — used
// to derive how many buckets can fit side-by-side without overlapping.
const MIN_MARKER_WIDTH_PX = 56;

export type { ReactionTimelineEvent };

export interface ReactionTimelineMarkerReaction {
  emoji: string;
  count: number;
}

export interface ReactionTimelineMarker {
  bucketIndex: number;
  leftPercent: number;
  reactions: ReactionTimelineMarkerReaction[];
  seekTime: number;
}

export const bucketCountForWidth = (containerWidthPx: number): number => {
  if (!containerWidthPx) {
    return MAX_BUCKET_COUNT;
  }

  const buckets = Math.floor(containerWidthPx / MIN_MARKER_WIDTH_PX);
  return Math.min(MAX_BUCKET_COUNT, Math.max(MIN_BUCKET_COUNT, buckets));
};

/**
 * Groups timestamped reaction events into fixed buckets across the video's
 * duration so the scrubber overlay can show a handful of markers instead of
 * one per click. `bucketCount` defaults to the max so callers/tests that
 * don't care about available screen width keep the finest-grained grouping.
 * Exported standalone so the bucketing math can be unit tested without
 * mounting the composable's store/DOM wiring.
 */
export const bucketReactionEvents = (
  events: ReactionTimelineEvent[],
  duration: number,
  findByName: (name: string) => string,
  bucketCount = MAX_BUCKET_COUNT
): ReactionTimelineMarker[] => {
  // Guards against stale reaction events whose name no longer resolves to a
  // displayable emoji (e.g. a reaction type that's since been retired).
  const resolvableEvents = events.filter(event => findByName(event.name));

  if (!duration || resolvableEvents.length === 0) {
    return [];
  }

  const buckets = new Map<number, Map<string, number>>();
  const earliestByBucket = new Map<number, number>();
  const timeSumByBucket = new Map<number, number>();
  const lastSeenIndexByBucket = new Map<number, Map<string, number>>();
  resolvableEvents.forEach((event, eventIndex) => {
    const bucketIndex = Math.min(bucketCount - 1, Math.floor((event.videoTime / duration) * bucketCount));
    const bucket = buckets.get(bucketIndex) || new Map<string, number>();
    bucket.set(event.name, (bucket.get(event.name) || 0) + 1);
    buckets.set(bucketIndex, bucket);
    earliestByBucket.set(bucketIndex, Math.min(earliestByBucket.get(bucketIndex) ?? event.videoTime, event.videoTime));
    timeSumByBucket.set(bucketIndex, (timeSumByBucket.get(bucketIndex) ?? 0) + event.videoTime);

    const lastSeenIndex = lastSeenIndexByBucket.get(bucketIndex) || new Map<string, number>();
    lastSeenIndex.set(event.name, eventIndex);
    lastSeenIndexByBucket.set(bucketIndex, lastSeenIndex);
  });

  return Array.from(buckets.entries()).map(([bucketIndex, countsByName]) => {
    // Most-frequent reaction first; ties go to whichever was added most recently.
    const lastSeenIndex = lastSeenIndexByBucket.get(bucketIndex) ?? new Map<string, number>();
    const reactions = Array.from(countsByName.entries())
      .sort((a, b) => {
        if (b[1] !== a[1]) {
          return b[1] - a[1];
        }

        return (lastSeenIndex.get(b[0]) ?? 0) - (lastSeenIndex.get(a[0]) ?? 0);
      })
      .map(([name, count]) => ({ emoji: findByName(name) || '', count }));

    // seekTime jumps to the earliest reaction in the bucket; leftPercent sits at the
    // bucket's average time so a marker stays near the cluster rather than its first event.
    const totalEvents = Array.from(countsByName.values()).reduce((sum, count) => sum + count, 0);
    const averageTime = (timeSumByBucket.get(bucketIndex) ?? 0) / totalEvents;

    return {
      bucketIndex,
      leftPercent: (averageTime / duration) * 100,
      reactions,
      seekTime: earliestByBucket.get(bucketIndex) ?? 0,
    };
  });
};

export const useReactionTimeline = () => {
  const itemStore = useViewerItemStore();
  const { id } = storeToRefs(itemStore);
  const reactionsStore = useReactionsStore();
  const { reactionEvents } = storeToRefs(reactionsStore);
  const { findByName } = useEmoji();

  const duration = ref(0);
  const progressHolder = ref<HTMLElement | null>(null);
  const containerWidth = ref(0);
  let viewerRoot: HTMLElement | null = null;
  let videoElement: HTMLVideoElement | null = null;
  let observer: MutationObserver | null = null;
  let resizeObserver: ResizeObserver | null = null;

  const updateDuration = () => {
    if (videoElement && !isNaN(videoElement.duration) && videoElement.duration > 0) {
      duration.value = videoElement.duration;
    }
  };

  const attachVideoListener = (video: HTMLVideoElement) => {
    if (videoElement === video) {
      return;
    }
    detachVideoListener();
    videoElement = video;
    video.addEventListener('durationchange', updateDuration);
    video.addEventListener('loadedmetadata', updateDuration);
    updateDuration();
  };

  const detachVideoListener = () => {
    if (videoElement) {
      videoElement.removeEventListener('durationchange', updateDuration);
      videoElement.removeEventListener('loadedmetadata', updateDuration);
      videoElement = null;
    }
  };

  const findTargets = () => {
    if (!viewerRoot) {
      return;
    }

    const video = viewerRoot.querySelector('video');
    if (video) {
      attachVideoListener(video);
    }

    if (!progressHolder.value) {
      progressHolder.value = viewerRoot.querySelector('.video-js .vjs-progress-holder');

      if (progressHolder.value) {
        resizeObserver = new ResizeObserver(([entry]) => {
          containerWidth.value = entry.contentRect.width;
        });
        resizeObserver.observe(progressHolder.value);
        containerWidth.value = progressHolder.value.clientWidth;
      }
    }

    if (videoElement && progressHolder.value) {
      observer?.disconnect();
      observer = null;
    }
  };

  // video.js can recreate the progress-holder DOM node on an item transition. The
  // MutationObserver above disconnects once it first finds both targets, so without
  // this reset a recreated node would leave the Teleport pointed at a detached
  // element with no rediscovery path.
  const resetTargets = () => {
    progressHolder.value = null;
    containerWidth.value = 0;

    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }

    if (!observer && viewerRoot) {
      observer = new MutationObserver(findTargets);
      observer.observe(viewerRoot, { childList: true, subtree: true });
    }
  };

  watch(id, () => {
    duration.value = 0;
    resetTargets();
  });

  const markers = computed((): ReactionTimelineMarker[] =>
    bucketReactionEvents(reactionEvents.value, duration.value, findByName, bucketCountForWidth(containerWidth.value))
  );

  const seekTo = (seekTime: number) => itemStore.setVideoTime(seekTime);

  onMounted(() => {
    viewerRoot = document.getElementById('viewer-content') || document.getElementById('main') || document.body;
    findTargets();

    if (videoElement && progressHolder.value) {
      return;
    }

    observer = new MutationObserver(findTargets);
    observer.observe(viewerRoot, { childList: true, subtree: true });
  });

  onUnmounted(() => {
    detachVideoListener();
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
  });

  return { markers, progressHolder, seekTo };
};
